import * as React from "react"
import { cva } from "class-variance-authority";
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,background-color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-red-100 text-red-600 [a&]:hover:bg-red-200 focus-visible:ring-red-500/20 dark:bg-red-950 dark:text-red-400 dark:[a&]:hover:bg-red-900",
        success:
          "bg-green-100 text-green-600 [a&]:hover:bg-green-200 focus-visible:ring-green-500/20 dark:bg-green-950 dark:text-green-400 dark:[a&]:hover:bg-green-900",
        pending:
          "bg-slate-100 text-slate-500 [a&]:hover:bg-slate-200 focus-visible:ring-slate-500/20 dark:bg-slate-800 dark:text-slate-400 dark:[a&]:hover:bg-slate-700",
        info:
          "bg-blue-100 text-blue-600 [a&]:hover:bg-blue-200 focus-visible:ring-blue-500/20 dark:bg-blue-950 dark:text-blue-400 dark:[a&]:hover:bg-blue-900",
        sky:
          "bg-sky-100 text-sky-600 [a&]:hover:bg-sky-200 focus-visible:ring-sky-500/20 dark:bg-sky-950 dark:text-sky-400 dark:[a&]:hover:bg-sky-900",
        purple:
          "bg-purple-100 text-purple-600 [a&]:hover:bg-purple-200 focus-visible:ring-purple-500/20 dark:bg-purple-950 dark:text-purple-400 dark:[a&]:hover:bg-purple-900",
        warning:
          "bg-amber-100 text-amber-600 [a&]:hover:bg-amber-200 focus-visible:ring-amber-500/20 dark:bg-amber-950 dark:text-amber-400 dark:[a&]:hover:bg-amber-900",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
      },
      size: {
        small: "px-2 py-0.5 text-xs gap-1 [&>svg]:size-3",
        medium: "px-2 py-0.5 text-sm gap-1 [&>svg]:size-3.5",
        large: "px-3 py-1.5 text-sm gap-2 [&>svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "small",
    },
  }
)

function Badge({
  className,
  variant = "default",
  size = "small",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props} />
  );
}

export { Badge, badgeVariants }
